import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Seo } from "../lib/seo";
import { api } from "../lib/api";

const fmtDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const DECISION_LABELS = {
  buying: "Buying",
  selling: "Selling",
  retaining: "Retaining",
  comparing: "Comparing markets",
  other: "Something else",
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [phase, setPhase] = useState("verifying");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setPhase("failed");
      return;
    }
    let attempts = 0;
    let timer;
    let degraded = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          try {
            const o = await api.get(`/suburbiq/orders/by-session/${sessionId}`);
            setOrder(o.data);
            setPhase("paid");
          } catch {
            setPhase("timeout");
          }
          return;
        }
        if (res.data.verification === "degraded") degraded += 1;
        if (res.data.payment_status === "failed" || res.data.payment_status === "expired") {
          setPhase("failed");
          return;
        }
        if (degraded >= 3) {
          setPhase("degraded");
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      attempts += 1;
      if (attempts >= 20) {
        setPhase("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <>
      <Seo title="SuburbIQ order confirmation | 80 Metrics" robots="noindex, nofollow" />
      <section className="bg-charcoal text-ivory min-h-[70vh]" data-testid="payment-success-page">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          {phase === "verifying" && (
            <div data-testid="payment-verifying">
              <p className="text-xs font-semibold tracking-[0.28em] uppercase text-gold">Confirming payment</p>
              <h1 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">Confirming your payment…</h1>
              <p className="mt-6 text-sm text-ivory/60">This takes a few seconds. Please don’t close this page.</p>
            </div>
          )}

          {phase === "paid" && order && (
            <div data-testid="payment-confirmed">
              <p className="text-xs font-semibold tracking-[0.28em] uppercase text-gold">Order confirmed</p>
              <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05]">
                Your SuburbIQ <span className="italic text-gold">is underway.</span>
              </h1>
              <dl className="mt-12 border-t border-white/15">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-5 border-b border-white/10">
                  <dt className="text-[11px] font-semibold tracking-[0.18em] uppercase text-ivory/50 pt-1">Agreed question</dt>
                  <dd className="sm:col-span-2 text-sm text-ivory/85 leading-relaxed" data-testid="confirmed-question">{order.question}</dd>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-5 border-b border-white/10">
                  <dt className="text-[11px] font-semibold tracking-[0.18em] uppercase text-ivory/50 pt-1">Suburb & property type</dt>
                  <dd className="sm:col-span-2 text-sm text-ivory/85" data-testid="confirmed-suburb">
                    {order.suburb} {order.state} {order.postcode}
                    {order.suburb2 ? ` vs ${order.suburb2} ${order.state2} ${order.postcode2}` : ""}
                    {" · "}{order.property_type === "house" ? "House" : "Unit"}
                  </dd>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-5 border-b border-white/10">
                  <dt className="text-[11px] font-semibold tracking-[0.18em] uppercase text-ivory/50 pt-1">Expected delivery</dt>
                  <dd className="sm:col-span-2 text-sm text-gold" data-testid="confirmed-delivery">{fmtDate(order.expected_delivery)}</dd>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-5 border-b border-white/10">
                  <dt className="text-[11px] font-semibold tracking-[0.18em] uppercase text-ivory/50 pt-1">Order reference</dt>
                  <dd className="sm:col-span-2 text-sm text-ivory/85" data-testid="confirmed-reference">{order.reference}</dd>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-5 border-b border-white/10">
                  <dt className="text-[11px] font-semibold tracking-[0.18em] uppercase text-ivory/50 pt-1">Tax invoice & receipt</dt>
                  <dd className="sm:col-span-2 text-sm text-ivory/85">
                    Your tax invoice and receipt confirmation will be sent to <span className="text-gold">{order.email}</span>.
                  </dd>
                </div>
              </dl>
              <p className="mt-10 text-sm text-ivory/60 leading-relaxed">
                We’ve received your question and payment. Matthew will review the question and relevant evidence — if
                clarification of wording or scope is required, we’ll contact you before preparation proceeds.
              </p>
              <Link to="/" className="mt-8 inline-block text-xs font-semibold tracking-[0.2em] uppercase text-ivory/70 hover:text-gold transition-colors duration-300" data-testid="success-home-link">
                ← Return to 80 Metrics
              </Link>
            </div>
          )}

          {phase === "degraded" && (
            <div data-testid="payment-degraded">
              <p className="text-xs font-semibold tracking-[0.28em] uppercase text-gold">Verification delayed</p>
              <h1 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">We can’t verify your payment just yet.</h1>
              <p className="mt-6 text-sm text-ivory/60 leading-relaxed max-w-xl">
                Our payment provider isn’t responding at the moment. If you completed payment, do not pay again —
                your order is safe and confirmation will follow. If you did not finish paying, you can return to SuburbIQ.
              </p>
              <Link
                to="/suburbiq"
                data-testid="degraded-suburbiq-link"
                className="mt-8 inline-flex px-8 py-4 bg-gold text-ink text-xs font-semibold tracking-[0.2em] uppercase transition-[background-color] duration-300 hover:bg-ivory"
              >
                Return to SuburbIQ
              </Link>
            </div>
          )}

          {(phase === "failed" || phase === "timeout") && (
            <div data-testid="payment-unconfirmed">
              <p className="text-xs font-semibold tracking-[0.28em] uppercase text-gold">Payment status</p>
              <h1 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">We couldn’t confirm your payment just yet.</h1>
              <p className="mt-6 text-sm text-ivory/60 leading-relaxed">
                Confirmation can take a moment. If you completed payment, do not pay again — your order is safe and
                confirmation will follow. If you didn’t finish paying, you can restart your SuburbIQ order.
              </p>
              <Link
                to="/suburbiq"
                data-testid="retry-suburbiq-link"
                className="mt-8 inline-flex px-8 py-4 bg-gold text-ink text-xs font-semibold tracking-[0.2em] uppercase transition-[background-color] duration-300 hover:bg-ivory"
              >
                Return to SuburbIQ
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
